<template>
  <div class="auth-input-root">
    <div class="auth-input-label">
      <p
        v-if="placeholderAndLabel.label"
        class="ma-0 auth-input-label input-label-default--text"
      >
        {{ $t(placeholderAndLabel.label) }}
      </p>
      <div v-if="necessary" class="necessary-doth" />
    </div>
    <div
      class="auth-input-container"
      :class="{
        'auth-input-container-error': errorState.errorStatus,
        'auth-input-container-success': successMessage
      }"
    >
      <input
        :value="value"
        :name="placeholderAndLabel.name"
        class="auth-input"
        :required="required"
        :class="{
          'auth-input-error': errorState.errorStatus
        }"
        :autofocus="autofocus"
        :type="type === 'password' ? localType : type"
        role="spinbutton"
        autocomplete="new-password"
        :placeholder="
          placeholderAndLabel.placeholder
            ? $t(placeholderAndLabel.placeholder)
            : ''
        "
        @input="
          handleChangeValue($event.target.value, placeholderAndLabel.name)
        "
        @focus="$emit('focus')"
        @blur="$emit('blur')"
      />
      <button
        v-if="type === 'password'"
        class="d-flex align-center auth-input-show-button"
        @keydown.space.prevent="handleShowPassword"
        @click="handleShowPassword"
      >
        <icon v-if="showPassword" name="eyeOutline24" class="auth-input-password-icon" />
        <icon v-else name="eyeSlashOutline24" class="auth-input-password-icon" />
      </button>
    </div>
      <div v-if="!checkErrorMessage && errorState.errorMessage" class="d-flex">
        <div>
          <icon v-if="showIcon" name="closeCircleFilled24" class="auth-input-icon-error mr-2"></icon>
        </div>
        <p class="ma-0 auth-input-error-messsage" v-html="$t(errorState.errorMessage)" />
      </div>
      <div v-if="successMessage" class="d-flex align-center">
        <icon name="checkFilled24" class="auth-input-icon-success mr-2"></icon>
        <p class="ma-0 success--text">
          {{ $t(successMessage) }}
        </p>
      </div>
    <!--Password Validation Start-->
    <slot />
    <!--Password Validation End-->
  </div>
</template>

<script>
import Icon from '@/assets/icons/Icon.vue'

export default {
  name: 'AuthInput',

  components: { Icon },

  props: {
    value: {
      type: [String, Number],
      required: false,
      default: ''
    },

    type: {
      type: String,
      required: false,
      default: 'text'
    },

    placeholderAndLabel: {
      type: Object,
      required: false,
      default: () => ({})
    },

    necessary: {
      type: Boolean,
      required: false,
      default: false
    },

    autofocus: {
      type: Boolean,
      required: false,
      default: false
    },

    errorState: {
      type: Object,
      required: false,
      default: () => ({})
    },

    successMessage: {
      type: String,
      required: false,
      default: ''
    },

    handleChangeValue: {
      type: Function,
      required: true
    },

    required: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      showPassword: true,
      localType: this.type,
      progressValue: 100
    }
  },

  computed: {
    showIcon() {
      return !this.errorState.errorMessage.includes('toManyAttempts')
    },

    checkErrorMessage() {
      return this.errorState.fromWhere === 'login' && this.errorState.errorMessage === 'auth.login.incorrectEmailOrPassword'
    }
  },

  methods: {
    handleShowPassword() {
      if (event.pointerType === '') return

      if (this.localType === 'password') {
        this.localType = 'text'
      } else {
        this.localType = 'password'
      }

      this.showPassword = !this.showPassword
    }
  }
}
</script>

<style scoped lang='scss'>
.auth-input-root {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-family: 'Roboto', sans-serif;
  width: 100%;
}

.auth-input-container > input:focus-visible {
  outline: none;
}

.auth-input {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  width: 100%;
  color: var(--v-input-foreground-typed--text-base);
}

.auth-input-container-error {
  border: 1px solid var(--v-input-border-error-base) !important;
}

.auth-input-container-success {
  border: 1px solid var(--v-input-border-success-base) !important;
}

.auth-input-error {
  color: var(--v-input-foreground-error-base) !important;
}

.auth-input-error-messsage {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  color: var(--v-text-error-base);
}

.auth-input-container {
  display: flex;
  justify-content: space-between;
  padding: 14px 16px;
  height: 48px;
  width: 100%;
  border: 1px solid #dedee0;
  border-radius: 8px;
}

.auth-input-label {
  display: flex;
  align-items: center;
  gap: 4px;
}

.auth-input-label {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

.necessary-doth {
  display: inline;
  background-color: #f5222d;
  width: 5px;
  height: 5px;
  border-radius: 100%;
}

.auth-input-icon-error {
  width: 16px;
  height: 16px;
  fill: var(--v-icon-error-base);
  align-self: self-start;
  margin-top: 1px;
}

.auth-input-icon-success {
  width: 14px;
  color: var(--v-icon-success-base);
}

.auth-input-password-icon {
  fill: var(--v-input-foreground-additional-base);
}

.auth-input-container:focus-within {
  border: 1px solid var(--v-input-border-focused-base);
}

.auth-input:focus::placeholder {
  color: transparent;
}

.auth-input-show-button {
  width: 20px;
  height: 20px;
}
</style>
